import React from "react";
import { Headline2, Paragraph } from '@cdm/quickenloans-library/lib';
import { testAttributes, navigationIds } from '../Constants';
import { readingSectionContainer, blockContent } from './ReadingSection.module.scss';

const CustomLink = (title, link) => (
    <a href={link}>{title}</a>
);
const ReadingSection = () => (
    <div data-testid={testAttributes.RATES_ARTICLES_READING_SECTION} className={readingSectionContainer}>
        <div className={blockContent}>
            <Headline2 id={navigationIds.HOW_TO_KNOW_MORTGAGE_RATE}>How To Know What Your Mortgage Rate Will Be</Headline2>
            <Paragraph>
                The interest rates and annual percentage rates for the mortgage options listed above are averages. The interest rate you’re offered will depend on factors such as your credit score, loan amount, down payment and loan type.
            </Paragraph>
            <Paragraph>
                We can give you a better idea of the interest rate you should expect by matching you with the lenders in our network. After you enter some basic information, we’ll find lenders that fit your financial needs and can explain your loan options.
            </Paragraph>
        </div>

        <div>
            <Headline2 id={navigationIds.CHOOSING_THE_RIGHT_MORTGAGE}>Choosing The Right Mortgage</Headline2>
            <Paragraph>
                Different mortgage options serve specific types of borrowers and circumstances:
            </Paragraph>
            <ul>
                <li>{CustomLink('Conventional loans', '/learn/conventional-loan')} are the most common and tend to be cheaper than FHA loans, but they have stricter eligibility requirements. </li>
                <li>{CustomLink('FHA loans', '/home-loans/fha-loan')} have looser credit requirements. These mortgages backed by the federal Housing Administration can be cheaper than conventional loans for borrowers with a lower credit score and smaller down payment.</li>
                <li>{CustomLink('VA loans', '/home-loans/va-loan')} are insured by the Department of Veterans Affairs and available only to eligible military personnel, veteran and their surviving spouses. </li>
                <li>{CustomLink('Jumbo loans', '/home-loans/jumbo-mortgage')} exceed the Federal housing finance Agency’s limits on conforming conventional loans. You may need a jumbo loan if you’re buying a refinancing a home in a high-cost area.</li>
            </ul>
        </div>

        <div>
            <Headline2 id={navigationIds.CONSISTENT_MONTHLY_PAYMENTS}>Consistent Monthly Payments</Headline2>
            <Paragraph>
                If you aren’t sure whether to go with a fixed-rate or adjustable-rate mortgage, here are the key differences:
            </Paragraph>
            <ul>
                <li>With a <strong>fixed-rate mortgage</strong>, your interest rate is set when you close and never changes. This gives you a consistent principal and interest payment every month. </li>
                <li>With an <strong>ARM</strong>, your interest rate remains the same for an introductory period but then wll adjust every so often, causing your monthly payment to fluctuate. </li>
            </ul>
            <Paragraph>If you have an arm and would like more predictable monthly payments, you may want to refinance to a fixed-rate mortgage</Paragraph>
        </div>

        <div>
            <Headline2 id={navigationIds.REFINANCE_YOUR_MORTGAGE}>Refinance Your Mortgage</Headline2>
            <Paragraph>When you {CustomLink('refinance','/refinance')}, you replace your current mortgage with a new one that has more favorable terms for you.</Paragraph>
        </div>

        <div>
            <Headline2 id={navigationIds.REASONS_TO_REFINANCE}>Reasons To Refinance</Headline2>
            <Paragraph>
                Here are some {CustomLink('reasons you may want to refinance', '/learn/reasons-to-refinance-your-home')}:
            </Paragraph>
            <ul>
                <li>Lower your interest rate and monthly payment.</li>
                <li>Pay off your mortgage sooner and save on interest.</li>
                <li>Switch from an ARM to a fixed-rate mortgage.</li>
            </ul>
        </div>

        <div>
            <Headline2 id={navigationIds.SHOULD_REFINANCE_NOW}>Should You Refinance Now?</Headline2>
            <Paragraph>
                Here are some questions to ask if you’re thinking of refinancing:
            </Paragraph>
            <ul>
                <li><strong>Are interest rates lower now than when you took out your mortgage?</strong> If so, refinancing could help you lower your monthly payment and save money on interest.</li>
                <li><strong>Are you planning to move soon?</strong> If you are, you may not have enough time to recoup the upfront costs of refinancing.</li>
                <li><strong>Has your credit score improved?</strong> If your credit score is higher than when you first took out your mortgage, you could get a lower interest rate by refinancing.</li>
                <li><strong>Does your mortgage have a prepayment penalty?</strong> If you’ll be charged for paying off your mortgage ahead of schedule, it may not make sense to refinance to reduce your loan term.</li>
            </ul>
        </div>

        <div>
            <Headline2 id={navigationIds.STEPS_TO_REFINANCE}>Steps To Refinance</Headline2>
            <Paragraph>
                If you think refinancing is right for you, here’s how to make it happen:
            </Paragraph>
            <ol>
                <li><strong>Choose your refinance type.</strong> A rate-and-term refinance changes your interest rate and loan term, while a cash-out refinance allows you to borrow some of your equity as cash.</li>
                <li>{CustomLink('Pick a lender','/refinance/compare-mortgage-refinance-lenders')}. You can refinance with your current lender, but you don’t have to. Shop around and request estimates from a few different lenders to make sure you’re getting the best deal.</li>
                <li><strong>Gather the necessary documents.</strong> Your lender will require recent documentation of your income, employment, savings and debts.</li>
                <li><strong>Go through underwriting.</strong> The lender will verify your financial information and order a home appraisal.</li>
                <li><strong>Close on the new loan.</strong> You’ll sign all the necessary paperwork, pay closing costs and finalize your refinance.</li>
            </ol>
        </div>
    </div>
);

export default ReadingSection;